const baseURL = window.location.origin;

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * The Leo MCP connector URL that users paste into Claude → Settings → Connectors.
 * It lives on the same host as the API, under /mcp (e.g.
 * https://uat-api.percuity.ai/api → https://uat-api.percuity.ai/mcp). Single
 * source of truth — the MCP settings tab and the Connections → Claude card both
 * read this, so the advertised URL never drifts.
 */
export const getMcpConnectorUrl = (): string =>
  `${(API_BASE_URL || '').replace(/\/api\/?$/, '')}/mcp`;
export const APP_ENV = import.meta.env.VITE_APP_ENV;
const appHost = window.location.hostname.toLowerCase();
export const IS_UAT_ENV =
  APP_ENV?.toLowerCase() === 'uat' || appHost.startsWith('uat.') || appHost.includes('.uat.');
export const APP_URL = `${baseURL}`; //import.meta.env.VITE_APP_URL;

export const GOOGLE_REDIRECT_URL = `${baseURL}`; //import.meta.env.VITE_GOOGLE_REDIRECT_URL;
export const AUTH_CALLBACK_URL = `${baseURL}/auth-callback`; //import.meta.env.VITE_AUTH_CALLBACK_URL;

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
export const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET;
export const GOOGLE_AD_CLIENT_ID = import.meta.env.VITE_GOOGLE_AD_CLIENT_ID;
export const GOOGLE_AD_CLIENT_SECRET = import.meta.env
  .VITE_GOOGLE_AD_CLIENT_SECRET;
export const META_APP_ID = import.meta.env.VITE_META_APP_ID;
export const META_CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID;
// TIKTOK_APP_ID is public — it rides the auth URL. There is deliberately no TIKTOK_SECRET
// here: the auth-code→token exchange happens server-side in `oauth_service.py`, reading
// TIKTOK_SECRET from Secrets Manager. Anything exported from this file is compiled into the
// browser bundle, so a client secret cannot live in it.
export const TIKTOK_APP_ID = import.meta.env.VITE_TIKTOK_APP_ID;
export const AMAZON_CLIENT_ID = import.meta.env.VITE_AMAZON_CLIENT_ID;
export const STRIPE_PUBLISHABLE_KEY = import.meta.env
  .VITE_STRIPE_PUBLISHABLE_KEY;
export const STRIPE_PRICING_TABLE_ID = import.meta.env
  .VITE_STRIPE_PRICING_TABLE_ID;
export const hidePages = import.meta.env.VITE_HIDE_PAGES?.split(',') || [];
export const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID;
export const HUBSPOT_CLIENT_ID = import.meta.env.VITE_HUBSPOT_CLIENT_ID;
export const REDDIT_CLIENT_ID = import.meta.env.VITE_REDDIT_CLIENT_ID;
export const BING_ADS_CLIENT_ID = import.meta.env.VITE_BING_ADS_CLIENT_ID;
export const MICROSOFT_ADS_CLIENT_ID = import.meta.env.VITE_MICROSOFT_ADS_CLIENT_ID;
export const SHOPIFY_CLIENT_ID = import.meta.env.VITE_SHOPIFY_CLIENT_ID;
export const GOHIGHLEVEL_CLIENT_ID = import.meta.env.VITE_GOHIGHLEVEL_CLIENT_ID;
export const SLACK_CLIENT_ID = import.meta.env.VITE_SLACK_CLIENT_ID;
export const X_ADS_CONSUMER_KEY = import.meta.env.VITE_X_ADS_CONSUMER_KEY;
// Salesforce: ONE Percuity-owned Connected App shared by all customers. The
// client_id is public (safe on the frontend — the secret stays server-side).
// login.salesforce.com is the authorize host for any production org; override
// via env only to point at test.salesforce.com for a sandbox connect.
export const SALESFORCE_CLIENT_ID = import.meta.env.VITE_SALESFORCE_CLIENT_ID;
export const SALESFORCE_LOGIN_URL =
  import.meta.env.VITE_SALESFORCE_LOGIN_URL || 'https://login.salesforce.com';

// Cognito federated login configuration
export const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
export const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
export const COGNITO_CALLBACK_URL = import.meta.env.VITE_COGNITO_CALLBACK_URL;
export const COGNITO_LOGOUT_URL = import.meta.env.VITE_COGNITO_LOGOUT_URL;

export const DSN = import.meta.env.VITE_SENTRY_DSN;
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
export const IS_SENTRY_DISABLED = import.meta.env.VITE_IS_SENTRY_DISABLED;

export const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY || import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
export const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ||
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
  'https://p.percuity.ai';
export const FEATURES = {
  DIRECT_S3_UPLOAD: import.meta.env.VITE_DIRECT_S3_UPLOAD == 'true' || false,
};

export const MAIN_APP_SIGNUP_URL = `${baseURL.replace(/\/$/, '')}/auth/signup`;

/** Where to send users when they log out (marketing/landing site). */
const defaultLandingUrl =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4321'
    : 'https://www.percuity.ai';
export const LANDING_PAGE_URL =
  import.meta.env.VITE_LANDING_PAGE_URL || defaultLandingUrl;

/** Marketing-site URL used to derive legal links. Production fallback is percuity.ai. */
const marketingSiteUrl =
  import.meta.env.VITE_MARKETING_SITE_URL || 'https://www.percuity.ai';
export const TERMS_OF_SERVICE_URL = `${marketingSiteUrl}/terms-of-service`;
export const PRIVACY_POLICY_URL = `${marketingSiteUrl}/privacy-policy`;
export const SECURITY_EMAIL =
  import.meta.env.VITE_SECURITY_EMAIL || 'security@percuity.ai';

export const DEFAULT_BRANDING_COMPANY_NAME =
  import.meta.env.VITE_DEFAULT_BRANDING_COMPANY_NAME || 'fibonaccidev';

export const DISABLE_BILLING_PAGE_FOR_COMPANIES =
  import.meta.env.VITE_DISABLE_BILLING_PAGE_FOR_COMPANIES?.split(',') || [];

// Maintenance banner configuration
export const MAINTENANCE_ENABLED =
  import.meta.env.VITE_MAINTENANCE_ENABLED === 'true';
export const MAINTENANCE_START = import.meta.env.VITE_MAINTENANCE_START || '';
export const MAINTENANCE_END = import.meta.env.VITE_MAINTENANCE_END || '';

// Environment variables debugging - only in development
if (typeof window !== 'undefined' && APP_ENV === 'development') {
  const logEnvVars = () => {
    console.group('🔧 Environment Variables Debug (Dev Only)');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('APP_ENV:', APP_ENV);
    // Client IDs are public but limit logging in production
    console.log('META_APP_ID:', META_APP_ID ? '***SET***' : 'undefined');
    console.log('META_CONFIG_ID:', META_CONFIG_ID ? '***SET***' : 'undefined');
    console.log('LINKEDIN_CLIENT_ID:', LINKEDIN_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('TIKTOK_APP_ID:', TIKTOK_APP_ID ? '***SET***' : 'undefined');
    console.log('GOOGLE_CLIENT_ID:', GOOGLE_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('AMAZON_CLIENT_ID:', AMAZON_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('HUBSPOT_CLIENT_ID:', HUBSPOT_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('REDDIT_CLIENT_ID:', REDDIT_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('SHOPIFY_CLIENT_ID:', SHOPIFY_CLIENT_ID ? '***SET***' : 'undefined');
    console.log('STRIPE_PUBLISHABLE_KEY:', STRIPE_PUBLISHABLE_KEY ? '***SET***' : 'undefined');
    console.log('SENTRY_DSN:', SENTRY_DSN ? '***SET***' : 'undefined');
    console.groupEnd();
  };
  // Log immediately and then every 10 seconds (only in dev)
  logEnvVars();
  setInterval(logEnvVars, 10000);
}